import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Team role from the team management system
 */
export type TeamRole = 'Admin' | 'Regular' | 'ReadOnly';

/**
 * Team membership status
 */
export type TeamStatus = 'unknown' | 'checking' | 'member' | 'not_in_team' | 'deactivated';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'staff' | 'volunteer' | 'readonly';
  avatar?: string;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;

  // Team membership
  teamStatus: TeamStatus;
  teamRole: TeamRole | null;
  teamMemberId: string | null;

  // Session timeout modal
  showSessionTimeout: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setTeamMembership: (status: TeamStatus, role?: TeamRole | null, memberId?: string | null) => void;
  setShowSessionTimeout: (show: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: false,
      error: null,
      teamStatus: 'unknown',
      teamRole: null,
      teamMemberId: null,
      showSessionTimeout: false,

      setUser: (user) => set({ user, error: null }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setTeamMembership: (status, role = null, memberId = null) =>
        set({ teamStatus: status, teamRole: role, teamMemberId: memberId }),
      setShowSessionTimeout: (show) => set({ showSessionTimeout: show }),
      logout: () =>
        set({
          user: null,
          error: null,
          teamStatus: 'unknown',
          teamRole: null,
          teamMemberId: null,
          showSessionTimeout: false,
        }),
    }),
    {
      name: 'sca-uk-auth',
      storage: createJSONStorage(() => sessionStorage),
      // Persist user and team membership data
      partialize: (state) => ({
        user: state.user,
        teamStatus: state.teamStatus,
        teamRole: state.teamRole,
        teamMemberId: state.teamMemberId,
      }),
    }
  )
);
